import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { RichTextEditor } from '@/shared/ui/rich-text-editor'

type Props = {
  readonly candidateId: Id<'candidates'>
}

export const CandidateNotesThread = ({ candidateId }: Props) => {
  const [noteBody, setNoteBody] = useState('')
  const [editorKey, setEditorKey] = useState(0)

  const { data: notes = [] } = useQuery(
    convexQuery(api.features.candidates.candidate_notes.listMyCandidateNotes, {
      candidateId,
    }),
  )

  const { mutateAsync: addNote } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidate_notes.addMyCandidateNote,
    ),
  })

  const { mutateAsync: deleteNote } = useMutation({
    mutationFn: useConvexMutation(
      api.features.candidates.candidate_notes.deleteMyOwnCandidateNote,
    ),
  })

  const handleAddNote = async (): Promise<void> => {
    const trimmed = noteBody.trim()
    if (!trimmed) return
    await addNote({ candidateId, body: noteBody })
    setNoteBody('')
    setEditorKey((k) => k + 1)
  }

  const formatDate = (timestampMs: number): string => {
    return new Date(timestampMs).toLocaleString()
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-medium text-primary">Notes</h3>

      {notes.length === 0 ? (
        <p className="text-xs text-primary/40">No notes yet.</p>
      ) : (
        <ol className="flex flex-col gap-2">
          {notes.map((note) => (
            <li
              key={note._id}
              className="flex items-start justify-between gap-3 rounded-md border border-border bg-background px-3 py-2"
            >
              <div className="flex flex-col gap-0.5">
                <div
                  className="text-sm text-primary [&_ol]:list-decimal [&_ol]:pl-4 [&_ul]:list-disc [&_ul]:pl-4"
                  dangerouslySetInnerHTML={{ __html: note.body }}
                />
                <p className="text-xs text-primary/50">
                  {formatDate(note.createdAt)}
                </p>
              </div>
              <button
                type="button"
                className="shrink-0 text-xs text-primary/40 hover:text-red-600"
                onClick={() =>
                  void deleteNote({ noteId: note._id as Id<'candidateNotes'> })
                }
              >
                Delete
              </button>
            </li>
          ))}
        </ol>
      )}

      <div className="flex flex-col gap-2">
        <RichTextEditor
          key={editorKey}
          initialContent={noteBody}
          onChange={setNoteBody}
          placeholder="Add a note..."
          minHeightClassName="min-h-16"
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => void handleAddNote()}
            disabled={!noteBody.trim()}
          >
            Add
          </Button>
        </div>
      </div>
    </div>
  )
}
